/* Atlas v3 — theme init + helpers.
   Day ('ink') = no data-theme attr (true-black/amber default in v3.css).
   Night ('night') = data-theme="night" → the violet after-hours palette.
   Auto ('auto') = follow the device's LOCAL CLOCK — Night in the evening, Day
   otherwise. Pure new Date() (the phone's own time + timezone, no permission /
   GPS / network), so it's true set-and-forget and timezone-correct everywhere.
   The stored CHOICE ('ink' | 'night' | 'auto') is persisted to
   localStorage('atlas_theme'); the resolved theme is applied at startup by
   initTheme() (called in main.jsx) before any v3 screen paints. */

const KEY = 'atlas_theme'

// Local-clock evening window for Auto. Night runs NIGHT_START..midnight..NIGHT_END.
// A fixed window (not true solar sunset — that needs location) is the trade for
// asking no permissions. Tune here.
const NIGHT_START = 19 // 7pm
const NIGHT_END = 7    // 7am

// The user's stored CHOICE. Back-compat: pre-Auto values were only ink/night.
export function getStoredMode() {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'night' || v === 'auto' ? v : 'ink'
  } catch {
    return 'ink'
  }
}

function clockIsNight() {
  const h = new Date().getHours()
  return h >= NIGHT_START || h < NIGHT_END
}

// Resolve a choice → the concrete theme to paint. 'auto' reads the local clock.
export function resolveTheme(mode = getStoredMode()) {
  if (mode === 'night') return 'night'
  if (mode === 'auto') return clockIsNight() ? 'night' : 'ink'
  return 'ink'
}

// Paint the resolved theme onto the document root (no persistence).
function paint(theme) {
  if (typeof document === 'undefined') return
  if (theme === 'night') document.documentElement.dataset.theme = 'night'
  else delete document.documentElement.dataset.theme
}

// Persist the user's choice + paint it immediately.
export function setThemeMode(mode) {
  const m = mode === 'night' || mode === 'auto' ? mode : 'ink'
  try { localStorage.setItem(KEY, m) } catch {}
  paint(resolveTheme(m))
}

// Re-evaluate Auto while the app is open so it flips at the 7pm/7am boundary
// (and when reopened after the boundary) without a reload. No-ops unless the
// stored choice is 'auto'. Idempotent — safe to call more than once.
let watching = false
function startAutoWatch() {
  if (watching || typeof window === 'undefined') return
  watching = true
  const reapply = () => { if (getStoredMode() === 'auto') paint(resolveTheme('auto')) }
  try { setInterval(reapply, 5 * 60 * 1000) } catch {}
  try { document.addEventListener('visibilitychange', reapply) } catch {}
}

// Run once at startup — paint the resolved theme + start the Auto watcher.
export function initTheme() {
  if (typeof document === 'undefined') return
  paint(resolveTheme())
  startAutoWatch()
}

/* Viewport height for the v3 screen roots (they read height: var(--v3-vh)).
   The unit is chosen in v3.css: 100dvh in a browser tab, 100vh in an installed
   PWA. On iOS the display-mode media query can be flaky, but navigator.standalone
   is reliable — so re-assert 100vh here for the home-screen app. This sets a
   UNIT, never a measured pixel height (measuring reads iOS's short innerHeight).

   Also fires a 2px scroll nudge to settle WebKit's viewport on first paint.
   Run once from main.jsx. */
export function initViewportFix() {
  if (typeof window === 'undefined') return
  if (navigator.standalone) {
    document.documentElement.style.setProperty('--v3-vh', '100vh')
  }
  const nudge = () => {
    const de = document.documentElement
    const prev = de.style.minHeight
    de.style.minHeight = 'calc(100% + 2px)'
    window.scrollTo(0, 1)
    window.scrollTo(0, 0)
    de.style.minHeight = prev
  }
  nudge()
  requestAnimationFrame(nudge)
  window.addEventListener('pageshow', nudge)
  window.addEventListener('orientationchange', () => setTimeout(nudge, 120))
  for (const ms of [120, 360, 900]) setTimeout(nudge, ms)
}
